import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Collapse,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Photo as PhotoIcon,
  Refresh as RefreshIcon,
  Public as PublicIcon,
  SignalCellularAlt as SignalIcon,
  Close as CloseIcon,
  Router as RouterIcon,
  Speed as SpeedIcon,
  Wifi as WifiIcon,
} from '@mui/icons-material';
import { api } from '../utils/api';
import toast from 'react-hot-toast';

// Helper function to convert country code to flag emoji
const getCountryFlag = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return '🌐';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// Common country names for display
const COUNTRY_NAMES = {
  VN: 'Vietnam',
  TH: 'Thailand',
  ID: 'Indonesia',
  MY: 'Malaysia',
  PH: 'Philippines',
  SG: 'Singapore',
  US: 'United States',
  GB: 'United Kingdom',
  DE: 'Germany',
  FR: 'France',
  JP: 'Japan',
  KR: 'South Korea',
  AU: 'Australia',
  IN: 'India',
  BR: 'Brazil',
  CN: 'China',
  IL: 'Israel',
  RU: 'Russia',
  CA: 'Canada',
  MX: 'Mexico',
  ES: 'Spain',
  IT: 'Italy',
  NL: 'Netherlands',
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  FI: 'Finland',
  PL: 'Poland',
  TR: 'Turkey',
  AE: 'UAE',
  SA: 'Saudi Arabia',
  EG: 'Egypt',
  ZA: 'South Africa',
  NG: 'Nigeria',
  KE: 'Kenya',
  AR: 'Argentina',
  CL: 'Chile',
  CO: 'Colombia',
  PE: 'Peru',
  NZ: 'New Zealand',
  HK: 'Hong Kong',
  TW: 'Taiwan',
  BD: 'Bangladesh',
  PK: 'Pakistan',
  UA: 'Ukraine',
  CZ: 'Czech Republic',
  AT: 'Austria',
  CH: 'Switzerland',
  BE: 'Belgium',
  PT: 'Portugal',
  GR: 'Greece',
  RO: 'Romania',
  HU: 'Hungary',
  IE: 'Ireland',
};


const DomainAnalyzer = () => {
  const [url, setUrl] = useState('');
  const [selectedCarriers, setSelectedCarriers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [expandedResults, setExpandedResults] = useState({});
  const [screenshotDialog, setScreenshotDialog] = useState({ open: false, url: '', title: '' });
  const [maxRetries, setMaxRetries] = useState(15);
  const [progressLogs, setProgressLogs] = useState([]);
  const [currentCarrier, setCurrentCarrier] = useState(null);
  const [currentIP, setCurrentIP] = useState(null);
  const [asnRetries, setAsnRetries] = useState(0);
  const [latestScreenshot, setLatestScreenshot] = useState(null);
  const [latestScreenshotCarrier, setLatestScreenshotCarrier] = useState('');
  const [currentStage, setCurrentStage] = useState('');
  const [currentAttempt, setCurrentAttempt] = useState({ current: 0, max: 0 });
  const [carrierProgress, setCarrierProgress] = useState({ current: 0, total: 0 });
  const [browseCountry, setBrowseCountry] = useState('');
  const [countryAsns, setCountryAsns] = useState([]);
  const [loadingAsns, setLoadingAsns] = useState(false);
  const [asnSearchQuery, setAsnSearchQuery] = useState('');
  const [asnSearchResults, setAsnSearchResults] = useState([]);
  const [searchingAsns, setSearchingAsns] = useState(false);
  const [allCountries, setAllCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  
  // ASN Probe state
  const [probeCountry, setProbeCountry] = useState('');
  const [probeRotations, setProbeRotations] = useState(50);
  const [probing, setProbing] = useState(false);
  const [probeResults, setProbeResults] = useState(null);
  const [probeProgress, setProbeProgress] = useState({ current: 0, total: 0, uniqueAsns: 0 });
  const [probeLogs, setProbeLogs] = useState([]);
  
  // Retry single carrier state
  const [retryingCarrier, setRetryingCarrier] = useState(null); // { index, asn, retries }
  const [retryDialog, setRetryDialog] = useState({ open: false, carrier: null, index: null });
  const [retryMaxRetries, setRetryMaxRetries] = useState(30);
  
  const eventSourceRef = useRef(null);
  const retryEventSourceRef = useRef(null);
  const probeEventSourceRef = useRef(null);
  const progressEndRef = useRef(null);
  const probeLogsEndRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Initialize - load all available countries
  useEffect(() => {
    loadAllCountries();
  }, []);
  
  // Load all countries from the ASN database
  const loadAllCountries = async () => {
    setLoadingCountries(true);
    try {
      const data = await api.analyzer.getCountries();
      setAllCountries(data.all || []);
    } catch (error) {
      console.error('Failed to load countries:', error);
    } finally {
      setLoadingCountries(false);
    }
  };

  // Auto-scroll progress logs
  useEffect(() => {
    if (progressEndRef.current) {
      progressEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [progressLogs]);

  // Cleanup SSE on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (probeEventSourceRef.current) {
        probeEventSourceRef.current.close();
      }
      if (retryEventSourceRef.current) {
        retryEventSourceRef.current.close();
      }
    };
  }, []);

  // Warn user before navigating away during analysis
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (loading) {
        e.preventDefault();
        e.returnValue = 'Analysis is still running. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [loading]);

  // Load ASNs for a specific country
  const loadCountryAsns = async (countryCode) => {
    if (!countryCode) {
      setCountryAsns([]);
      return;
    }
    
    setLoadingAsns(true);
    try {
      const data = await api.analyzer.getCountryAsns(countryCode, 30);
      setCountryAsns(data.topIsps || []);
    } catch (error) {
      toast.error('Failed to load ASNs for country');
      setCountryAsns([]);
    } finally {
      setLoadingAsns(false);
    }
  };

  // Search ASNs by name
  const searchAsns = async (query) => {
    if (!query || query.length < 2) {
      setAsnSearchResults([]);
      return;
    }
    
    setSearchingAsns(true);
    try {
      const data = await api.analyzer.searchAsns(query, browseCountry || null);
      setAsnSearchResults(data.results || []);
    } catch (error) {
      setAsnSearchResults([]);
    } finally {
      setSearchingAsns(false);
    }
  };

  // Debounced ASN search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (asnSearchQuery) {
        searchAsns(asnSearchQuery);
      } else {
        setAsnSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [asnSearchQuery, browseCountry]);

  // Load ASNs when country changes
  useEffect(() => {
    if (browseCountry) {
      loadCountryAsns(browseCountry);
    }
  }, [browseCountry]);

  // Add custom ASN from search/browse
  const addCustomAsn = (asn, countryCode, name) => {
    const exists = selectedCarriers.some(c => c.asn === asn);
    if (exists) {
      toast.error('This ASN is already selected');
      return;
    }
    setSelectedCarriers([...selectedCarriers, { 
      country: countryCode, 
      asn: asn,
      name: name,
      custom: true 
    }]);
    toast.success(`Added ${name || `AS${asn}`}`);
  };

  // Remove an ASN from selection
  const removeAsn = (asn) => {
    setSelectedCarriers(selectedCarriers.filter(c => c.asn !== asn));
  };

  const runAnalysis = async () => {
    if (!url) {
      toast.error('Please enter a URL to analyze');
      return;
    }

    if (selectedCarriers.length === 0) {
      toast.error('Please select at least one carrier');
      return;
    }

    // Validate URL format
    let testUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      testUrl = `https://${url}`;
    }

    setLoading(true);
    setResults(null);
    setProgressLogs([]);
    setCurrentCarrier(null);
    setCurrentIP(null);
    setAsnRetries(0);
    setLatestScreenshot(null);
    setLatestScreenshotCarrier('');
    setCurrentStage('Initializing...');
    setCurrentAttempt({ current: 0, max: 0 });
    setCarrierProgress({ current: 0, total: selectedCarriers.length });

    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();

    // Close any existing SSE connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Build SSE URL with auth token
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({
      url: testUrl,
      carriers: JSON.stringify(selectedCarriers),
      maxRetries: maxRetries.toString()
    });
    
    const sseUrl = `/api/analyzer/analyze-stream?${params.toString()}`;
    
    // Create EventSource with auth header workaround (fetch + ReadableStream)
    try {
      const response = await fetch(sseUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Analysis failed');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const processStream = async () => {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                handleSSEEvent(data);
              } catch (e) {
                // Skip invalid JSON
              }
            }
          }
        }
      };

      await processStream();
    } catch (error) {
      toast.error(error.message || 'Analysis failed');
      setLoading(false);
    }
  };

  const handleSSEEvent = async (data) => {
    const timestamp = new Date().toLocaleTimeString();
    const msg = data.message || '';
    
    // Filter out noise messages
    const isNoise = (m) => {
      if (!m) return true;
      const noisePatterns = [
        /^Loaded \d+/i,
        /^Loading/i,
        /^Preload/i,
        /^\s*$/,
        /^╔/,
        /^║/,
        /^╚/,
        /D-ANALYZER/i,
        /^Detect censorship/i,
        /^Testing \d+ mobile/i,
        /^Target:/i,
        /^Mode:/i,
        /^Browser:/i,
        /^─+$/,
        /^\s*•\s/,
      ];
      return noisePatterns.some(p => p.test(m));
    };
    
    // Parse detailed info from messages
    const parseAsnMismatch = (m) => {
      const match = m.match(/got (AS\d+)\s*\(([^)]+)\),?\s*need (AS\d+)/i);
      if (match) return { gotAsn: match[1], gotIp: match[2], needAsn: match[3] };
      return null;
    };
    
    const parseAttempt = (m) => {
      const match = m.match(/attempt (\d+)\/(\d+)/i);
      if (match) return { current: parseInt(match[1]), max: parseInt(match[2]) };
      return null;
    };
    
    const parseCarrierProgress = (m) => {
      const match = m.match(/\[(\d+)\/(\d+)\]/);
      if (match) return { current: parseInt(match[1]), total: parseInt(match[2]) };
      return null;
    };
    
    switch (data.type) {
      case 'start':
        setCurrentStage('Starting analysis...');
        setCarrierProgress({ current: 0, total: data.carriers || selectedCarriers.length });
        setProgressLogs(prev => [...prev, { 
          time: timestamp, 
          type: 'info', 
          message: `🚀 Starting analysis of ${data.carriers} carrier(s)...` 
        }]);
        break;
        
      case 'carrier_start':
        if (data.carrierNum && data.carrierTotal) {
          setCarrierProgress({ current: data.carrierNum, total: data.carrierTotal });
        } else {
          const carrierProg = parseCarrierProgress(msg);
          if (carrierProg) {
            setCarrierProgress(carrierProg);
          }
        }
        setCurrentCarrier(data.carrier || msg);
        setCurrentIP(null);
        setAsnRetries(0);
        setCurrentAttempt({ current: 0, max: maxRetries });
        setCurrentStage('Finding correct ASN...');
        setProgressLogs(prev => [...prev, { 
          time: timestamp, 
          type: 'carrier', 
          message: `📡 Testing: ${data.carrier || msg}` 
        }]);
        break;
        
      case 'ip_found':
        setCurrentIP(data.ip);
        setProgressLogs(prev => [...prev, { 
          time: timestamp, 
          type: 'ip', 
          message: `🌐 Exit IP: ${data.ip}` 
        }]);
        break;
        
      case 'asn_retry':
        // Handle ASN mismatch with detailed info from server
        if (data.gotAsn && data.gotIp && data.needAsn) {
          setCurrentIP(data.gotIp);
          setCurrentStage(`ASN mismatch: got ${data.gotAsn}, need ${data.needAsn}`);
          setProgressLogs(prev => [...prev, { 
            time: timestamp, 
            type: 'retry', 
            message: `⟳ Got ${data.gotAsn} (${data.gotIp}) - need ${data.needAsn}` 
          }]);
        }
        // Handle attempt info
        if (data.attempt && data.maxAttempts) {
          setCurrentAttempt({ current: data.attempt, max: data.maxAttempts });
          setAsnRetries(data.attempt);
          if (!data.gotAsn) {
            setProgressLogs(prev => [...prev, { 
              time: timestamp, 
              type: 'retry', 
              message: `⟳ Rotating to new IP... (attempt ${data.attempt}/${data.maxAttempts})` 
            }]);
          }
        }
        // Fallback to parsing from message
        if (!data.gotAsn && !data.attempt) {
          const mismatch = parseAsnMismatch(msg);
          const attempt = parseAttempt(msg);
          
          if (mismatch) {
            setCurrentIP(mismatch.gotIp);
            setCurrentStage(`ASN mismatch: got ${mismatch.gotAsn}, need ${mismatch.needAsn}`);
            setProgressLogs(prev => [...prev, { 
              time: timestamp, 
              type: 'retry', 
              message: `⟳ Got ${mismatch.gotAsn} (${mismatch.gotIp}) - need ${mismatch.needAsn}` 
            }]);
          }
          
          if (attempt) {
            setCurrentAttempt(attempt);
            setAsnRetries(attempt.current);
          }
        }
        break;
        
      case 'warning':
        setCurrentStage('ASN not found in proxy pool');
        setProgressLogs(prev => [...prev, { 
          time: timestamp, 
          type: 'warning', 
          message: `⚠ ${msg}` 
        }]);
        break;
        
      case 'isp_info':
        setProgressLogs(prev => [...prev, { 
          time: timestamp, 
          type: 'info', 
          message: `📍 ${msg}` 
        }]);
        break;
        
      case 'progress':
      case 'log':
        if (isNoise(msg)) break;
        
        // Check for important messages
        if (msg.includes('Double verified') || msg.includes('ASN match')) {
          setCurrentStage('ASN verified! Running browser test...');
          setProgressLogs(prev => [...prev, { 
            time: timestamp, 
            type: 'success', 
            message: `✓ ${msg}` 
          }]);
        } else if (msg.includes('SKIPPED') || msg.includes('Could not find ASN')) {
          setCurrentStage('ASN not found in proxy pool');
          setProgressLogs(prev => [...prev, { 
            time: timestamp, 
            type: 'warning', 
            message: `⚠ ${msg}` 
          }]);
        } else if (msg.includes('ISP:')) {
          setProgressLogs(prev => [...prev, { 
            time: timestamp, 
            type: 'info', 
            message: `📍 ${msg}` 
          }]);
        } else if (data.stage === 'verifying') {
          setCurrentStage('Verifying ASN via ip-api.com...');
          setProgressLogs(prev => [...prev, { 
            time: timestamp, 
            type: 'info', 
            message: `🔍 ${msg}` 
          }]);
        } else {
          setProgressLogs(prev => [...prev, { 
            time: timestamp, 
            type: 'log', 
            message: msg 
          }]);
        }
        break;
        
      case 'asn_found':
      case 'asn_info':
        setCurrentStage('ASN verified! Running browser test...');
        setProgressLogs(prev => [...prev, { 
          time: timestamp, 
          type: 'success', 
          message: `✓ ${msg}` 
        }]);
        break;
        
      case 'result':
        const isBlocked = data.blocked || msg.includes('BLOCKED');
        const isAccessible = data.accessible || msg.includes('ACCESSIBLE');
        const isSkipped = data.skipped || msg.includes('SKIPPED');
        
        setCurrentStage(isBlocked ? 'BLOCKED' : isAccessible ? 'ACCESSIBLE' : isSkipped ? 'SKIPPED' : 'Complete');
        setProgressLogs(prev => [...prev, { 
          time: timestamp, 
          type: isBlocked ? 'blocked' : isSkipped ? 'warning' : 'accessible', 
          message: isBlocked ? `🚫 ${msg}` : isSkipped ? `⊘ ${msg}` : `✅ ${msg}` 
        }]);
        break;
      
      case 'screenshot':
        // New screenshot available - fetch with auth
        if (data.path) {
          setCurrentStage('Screenshot captured');
          const filename = data.path.split('/').pop();
          try {
            const blobUrl = await api.analyzer.fetchScreenshot(filename);
            if (blobUrl) {
              setLatestScreenshot(blobUrl);
              setLatestScreenshotCarrier(data.carrier || currentCarrier || 'Unknown');
            }
          } catch (e) {
            console.error('Failed to load screenshot:', e);
          }
          setProgressLogs(prev => [...prev, { 
            time: timestamp, 
            type: 'success', 
            message: `📷 Screenshot captured` 
          }]);
        }
        break;
        
      case 'success':
        setCurrentStage('Page loaded successfully');
        setProgressLogs(prev => [...prev, { 
          time: timestamp, 
          type: 'success', 
          message: `✓ ${msg}` 
        }]);
        break;
        
      case 'error_log':
        setProgressLogs(prev => [...prev, { 
          time: timestamp, 
          type: 'error', 
          message: `✗ ${msg}` 
        }]);
        break;
        
      case 'complete':
        setResults(data.result);
        setLoading(false);
        setCurrentStage('Analysis complete');
        // Don't clear screenshot - keep it for reference
        
        // Auto-expand results with blocking
        const expanded = {};
        data.result?.results?.forEach((r, i) => {
          if (r.blocking?.detected) {
            expanded[i] = true;
          }
        });
        setExpandedResults(expanded);

        if (data.result?.summary?.blocking_detected > 0) {
          toast.error(`Blocking detected on ${data.result.summary.blocking_detected} carrier(s)`);
        } else if (data.result?.summary?.successful > 0) {
          toast.success('Analysis complete - No blocking detected');
        }
        break;
        
      case 'error':
        toast.error(data.message || 'Analysis failed');
        setLoading(false);
        setCurrentStage('Error');
        break;
        
      default:
        break;
    }
  };

  const getStatusIcon = (result) => {
    if (result.asnNotFound) {
      return <WarningIcon sx={{ color: 'warning.main' }} />;
    }
    if (result.blocking?.detected) {
      return <ErrorIcon sx={{ color: 'error.main' }} />;
    }
    if (result.success) {
      return <CheckCircleIcon sx={{ color: 'success.main' }} />;
    }
    return <ErrorIcon sx={{ color: 'error.main' }} />;
  };

  const getStatusText = (result) => {
    if (result.asnNotFound) return 'SKIPPED';
    if (result.blocking?.detected) return 'BLOCKED';
    if (result.success) return 'ACCESSIBLE';
    return 'ERROR';
  };

  const getStatusColor = (result) => {
    if (result.asnNotFound) return 'warning';
    if (result.blocking?.detected) return 'error';
    if (result.success) return 'success';
    return 'error';
  };

  const openScreenshot = async (screenshotPath, title) => {
    if (!screenshotPath) return;
    const filename = screenshotPath.split('/').pop();
    try {
      const blobUrl = await api.analyzer.fetchScreenshot(filename);
      if (blobUrl) {
        setScreenshotDialog({ open: true, url: blobUrl, title });
      } else {
        toast.error('Screenshot not available');
      }
    } catch (e) {
      toast.error('Failed to load screenshot');
    }
  };

  // ASN Probe function
  const startProbe = () => {
    if (!probeCountry) {
      toast.error('Please select a country to probe');
      return;
    }
    
    setProbing(true);
    setProbeResults(null);
    setProbeLogs([]);
    setProbeProgress({ current: 0, total: probeRotations, uniqueAsns: 0 });
    
    const url = api.analyzer.probeAsnsStream(probeCountry, probeRotations);
    const eventSource = new EventSource(url);
    probeEventSourceRef.current = eventSource;
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'start') {
          setProbeLogs(prev => [...prev, { time: new Date(), message: data.message, type: 'info' }]);
        } else if (data.type === 'progress') {
          setProbeProgress({ 
            current: data.rotation, 
            total: data.total, 
            uniqueAsns: data.uniqueAsns || 0 
          });
          
          if (data.error) {
            setProbeLogs(prev => [...prev, { 
              time: new Date(), 
              message: `Rotation ${data.rotation}: Error - ${data.error}`, 
              type: 'error' 
            }]);
          } else if (data.asn) {
            setProbeLogs(prev => [...prev, { 
              time: new Date(), 
              message: `Rotation ${data.rotation}: ${data.ip} → AS${data.asn} (${data.name})`, 
              type: 'success',
              asn: data.asn,
              name: data.name
            }]);
          }
        } else if (data.type === 'complete') {
          setProbeResults(data.results);
          setProbeLogs(prev => [...prev, { 
            time: new Date(), 
            message: `Complete! Found ${data.results.length} unique ASNs from ${data.summary.totalRotations} rotations`, 
            type: 'info' 
          }]);
          setProbing(false);
          eventSource.close();
          toast.success(`Found ${data.results.length} available ASNs for ${probeCountry}`);
        } else if (data.type === 'error') {
          setProbeLogs(prev => [...prev, { time: new Date(), message: data.message, type: 'error' }]);
          setProbing(false);
          eventSource.close();
          toast.error(data.message);
        }
      } catch (e) {
        console.error('Failed to parse probe event:', e);
      }
    };
    
    eventSource.onerror = () => {
      setProbing(false);
      eventSource.close();
      toast.error('Connection lost during probe');
    };
  };
  
  const stopProbe = () => {
    if (probeEventSourceRef.current) {
      probeEventSourceRef.current.close();
    }
    setProbing(false);
    toast.info('Probe stopped');
  };
  
  // Add ASN from probe results to selected carriers
  const addProbeAsn = (asn) => {
    const exists = selectedCarriers.some(c => c.asn === asn.asn);
    if (exists) {
      toast.info('ASN already selected');
      return;
    }
    
    setSelectedCarriers(prev => [...prev, {
      country: asn.country || probeCountry,
      asn: asn.asn,
      name: asn.name,
      custom: true
    }]);
    toast.success(`Added ${asn.name} (AS${asn.asn})`);
  };
  
  // Retry a single skipped carrier
  const retrySkippedCarrier = (resultIndex, customRetries = null) => {
    const result = results?.results?.[resultIndex];
    if (!result || !url) return;
    
    const retriesToUse = customRetries || retryMaxRetries;
    const carrier = {
      country: result.proxy?.country || 'XX',
      asn: result.proxy?.expectedAsn || result.proxy?.asn,
      name: result.proxy?.label?.replace(/^[A-Z]{2} Mobile - /, '') || `AS${result.proxy?.expectedAsn}`
    };
    
    if (!carrier.asn) {
      toast.error('Cannot retry: ASN information missing');
      return;
    }
    
    setRetryingCarrier({ index: resultIndex, asn: carrier.asn, retries: retriesToUse });
    setRetryDialog({ open: false, carrier: null, index: null });
    
    // Reset progress state for this retry
    setCurrentCarrier(carrier.name);
    setCurrentIP(null);
    setCurrentStage('Starting retry...');
    setCurrentAttempt({ current: 0, max: retriesToUse });
    setProgressLogs([{ time: new Date(), message: `Retrying ${carrier.name} with ${retriesToUse} max retries...` }]);
    
    // Build the SSE URL for single carrier retry
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({
      url,
      carriers: JSON.stringify([carrier]),
      maxRetries: retriesToUse,
      token
    }).toString();
    
    const eventSource = new EventSource(`/api/analyzer/analyze-stream?${params}`);
    retryEventSourceRef.current = eventSource;
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'progress') {
          // Update progress logs
          const timestamp = new Date().toLocaleTimeString();
          let logMessage = data.message || '';
          
          if (data.ip) setCurrentIP(data.ip);
          if (data.carrier) setCurrentCarrier(data.carrier);
          if (data.stage) setCurrentStage(data.stage);
          if (data.attempt !== undefined && data.maxAttempts !== undefined) {
            setCurrentAttempt({ current: data.attempt, max: data.maxAttempts });
          }
          
          if (logMessage && !logMessage.includes('Loaded')) {
            setProgressLogs(prev => [...prev.slice(-50), { time: new Date(), message: logMessage }]);
          }
        } else if (data.type === 'complete') {
          // Update the specific result in the results array
          if (data.result?.results?.[0]) {
            const newResult = data.result.results[0];
            setResults(prev => {
              const updatedResults = [...prev.results];
              updatedResults[resultIndex] = newResult;
              return {
                ...prev,
                results: updatedResults,
                // Recalculate summary
                summary: {
                  ...prev.summary,
                  blocking_detected: updatedResults.filter(r => r.blocking?.detected).length,
                  successful: updatedResults.filter(r => r.success && !r.blocking?.detected).length,
                  skipped: updatedResults.filter(r => r.asnNotFound).length
                }
              };
            });
            
            if (newResult.asnNotFound) {
              toast.warning(`Still couldn't find ASN ${carrier.asn} after ${retriesToUse} retries`);
            } else if (newResult.blocking?.detected) {
              toast.error(`Blocking detected on ${carrier.name}`);
            } else if (newResult.success) {
              toast.success(`${carrier.name} - Site accessible!`);
            }
          }
          
          setRetryingCarrier(null);
          setCurrentCarrier(null);
          setCurrentIP(null);
          setCurrentStage('');
          setCurrentAttempt({ current: 0, max: 0 });
          eventSource.close();
        } else if (data.type === 'error') {
          toast.error(data.message || 'Retry failed');
          setRetryingCarrier(null);
          eventSource.close();
        }
      } catch (e) {
        console.error('Failed to parse retry event:', e);
      }
    };
    
    eventSource.onerror = () => {
      setRetryingCarrier(null);
      eventSource.close();
      toast.error('Connection lost during retry');
    };
  };
  
  // Open retry dialog with custom retry count option
  const openRetryDialog = (resultIndex) => {
    const result = results?.results?.[resultIndex];
    if (!result) return;
    
    setRetryDialog({
      open: true,
      carrier: result.proxy?.label || 'Unknown Carrier',
      index: resultIndex,
      asn: result.proxy?.expectedAsn || result.proxy?.asn
    });
    setRetryMaxRetries(30); // Default to higher retries for manual retry
  };

  const getCarrierInfo = (proxy) => {
    // Find carrier info from selected carriers
    const asn = proxy?.expectedAsn || proxy?.asn;
    if (!asn) return null;
    
    return selectedCarriers.find(c => c.asn === asn);
  };

  return (
    <Box>
      {/* URL Input Section */}
      {/* Main Input Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PublicIcon color="primary" />
            Domain Blocking Analyzer
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Detect ISP-level domain blocking across any country. Select ISPs/carriers from the database below and run the analysis.
          </Typography>

          {/* URL Input */}
          <TextField
            fullWidth
            label="URL to Analyze"
            placeholder="https://example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={loading}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          {/* Country and ISP Search */}
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SignalIcon fontSize="small" />
            Select ISPs/Carriers to Test
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Country selector - Autocomplete dropdown */}
            <Grid item xs={12} md={5}>
              <Autocomplete
                size="small"
                options={allCountries.map(c => ({
                  code: c.code,
                  name: COUNTRY_NAMES[c.code] || c.code,
                  count: c.count
                }))}
                getOptionLabel={(option) => {
                  if (!option) return '';
                  const name = option.name || COUNTRY_NAMES[option.code] || option.code;
                  return `${getCountryFlag(option.code)} ${name} (${option.code})`;
                }}
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter(option => 
                    option.name.toLowerCase().includes(searchTerm) ||
                    option.code.toLowerCase().includes(searchTerm)
                  );
                }}
                value={browseCountry ? { 
                  code: browseCountry, 
                  name: COUNTRY_NAMES[browseCountry] || browseCountry,
                  count: allCountries.find(c => c.code === browseCountry)?.count || 0
                } : null}
                onChange={(event, newValue) => {
                  setBrowseCountry(newValue?.code || '');
                }}
                loading={loadingCountries}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Country"
                    placeholder="Type country name or code (e.g., Israel, IL, Vietnam, VN)..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCountries ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '1.2rem' }}>{getCountryFlag(option.code)}</Typography>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.code} • {option.count?.toLocaleString()} ISPs
                      </Typography>
                    </Box>
                  </Box>
                )}
                isOptionEqualToValue={(option, value) => option?.code === value?.code}
                clearOnEscape
                autoHighlight
              />
            </Grid>

            {/* ISP selector - Autocomplete dropdown */}
            <Grid item xs={12} md={7}>
              <Autocomplete
                size="small"
                options={browseCountry ? countryAsns : []}
                getOptionLabel={(option) => 
                  option ? `${option.name} (AS${option.asn})` : ''
                }
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter(option => 
                    option.name?.toLowerCase().includes(searchTerm) ||
                    String(option.asn).includes(searchTerm)
                  );
                }}
                onChange={(event, newValue) => {
                  if (newValue && !selectedCarriers.some(c => c.asn === newValue.asn)) {
                    addCustomAsn(newValue.asn, newValue.countryCode || browseCountry, newValue.name);
                  }
                }}
                loading={loadingAsns}
                disabled={!browseCountry}
                value={null}
                blurOnSelect
                clearOnBlur
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={browseCountry ? `Select ISP from ${COUNTRY_NAMES[browseCountry] || browseCountry}` : "Select a country first"}
                    placeholder={browseCountry ? "Search and select ISP..." : ""}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: browseCountry ? (
                        <Typography sx={{ fontSize: '1.2rem', ml: 1 }}>{getCountryFlag(browseCountry)}</Typography>
                      ) : null,
                      endAdornment: (
                        <>
                          {loadingAsns ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const isSelected = selectedCarriers.some(c => c.asn === option.asn);
                  return (
                    <Box 
                      component="li" 
                      {...props} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        opacity: isSelected ? 0.5 : 1,
                        bgcolor: isSelected ? 'action.selected' : 'inherit'
                      }}
                    >
                      <Typography sx={{ fontSize: '1.2rem' }}>{getCountryFlag(option.countryCode || browseCountry)}</Typography>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2">
                          {option.name} {isSelected && '✓'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          AS{option.asn} • {option.prefixes?.toLocaleString() || 0} prefixes
                        </Typography>
                      </Box>
                      {!isSelected && (
                        <Chip label="+ Add" size="small" color="primary" variant="outlined" />
                      )}
                    </Box>
                  );
                }}
                isOptionEqualToValue={(option, value) => option?.asn === value?.asn}
                noOptionsText={browseCountry ? "No ISPs found" : "Select a country first"}
              />
            </Grid>
          </Grid>

          {/* Global ISP Search (across all countries) */}
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Or search ISP globally (any country)"
              placeholder="e.g., Verizon, Vodafone, Telkom, Cellcom..."
              value={asnSearchQuery}
              onChange={(e) => setAsnSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />,
                endAdornment: searchingAsns ? <CircularProgress size={20} /> : null
              }}
            />
          </Box>

          {/* Global Search Results */}
          {asnSearchResults.length > 0 && (
            <Paper variant="outlined" sx={{ mt: 2, maxHeight: 250, overflow: 'auto' }}>
              <List dense>
                {asnSearchResults.map((asn) => (
                  <ListItem
                    key={asn.asn}
                    secondaryAction={
                      <Button
                        size="small"
                        variant="contained"
                        color={selectedCarriers.some(c => c.asn === asn.asn) ? 'success' : 'primary'}
                        onClick={() => addCustomAsn(asn.asn, asn.countryCode, asn.name)}
                        disabled={selectedCarriers.some(c => c.asn === asn.asn)}
                      >
                        {selectedCarriers.some(c => c.asn === asn.asn) ? '✓ Added' : '+ Add'}
                      </Button>
                    }
                    sx={{ 
                      '&:hover': { bgcolor: 'action.hover' },
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Typography sx={{ fontSize: '1.5rem' }}>
                        {getCountryFlag(asn.countryCode)}
                      </Typography>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body1" fontWeight="medium">
                            {asn.name}
                          </Typography>
                          <Chip 
                            label={`AS${asn.asn}`} 
                            size="small" 
                            color="primary"
                            variant="outlined" 
                            sx={{ fontFamily: 'monospace' }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {getCountryFlag(asn.countryCode)} {COUNTRY_NAMES[asn.countryCode] || asn.countryCode} ({asn.countryCode}) • {asn.prefixes?.toLocaleString() || 0} IP prefixes
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}

          {/* Selected ASNs */}
          {selectedCarriers.length > 0 && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'success.main', borderRadius: 1, color: 'success.contrastText' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon fontSize="small" />
                  Selected ISPs ({selectedCarriers.length}):
                </Typography>
                <Button 
                  size="small" 
                  variant="outlined"
                  color="inherit"
                  onClick={() => setSelectedCarriers([])}
                >
                  Clear All
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedCarriers.map((carrier) => (
                  <Chip
                    key={carrier.asn}
                    icon={<Typography sx={{ fontSize: '1rem', ml: 0.5 }}>{getCountryFlag(carrier.country)}</Typography>}
                    label={`${carrier.name || `AS${carrier.asn}`} (${carrier.country})`}
                    size="small"
                    sx={{ bgcolor: 'background.paper', color: 'text.primary' }}
                    onDelete={() => removeAsn(carrier.asn)}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Run Analysis Section */}
          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  ASN Retries:
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  value={maxRetries}
                  onChange={(e) => setMaxRetries(Math.max(5, Math.min(50, parseInt(e.target.value) || 15)))}
                  inputProps={{ min: 5, max: 50, style: { width: 60, textAlign: 'center' } }}
                  disabled={loading}
                />
                <Tooltip title="Number of times to retry finding the correct ASN. Higher = more likely to find, but slower. Default: 15">
                  <IconButton size="small">
                    <WarningIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={runAnalysis}
              disabled={loading || !url || selectedCarriers.length === 0}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              sx={{ minWidth: 200 }}
            >
              {loading ? 'Analyzing...' : `Analyze (${selectedCarriers.length} ISPs)`}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Loading State with Live Progress */}
      {loading && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            {/* Current Status Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CircularProgress size={40} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6">
                  Analyzing Domain...
                </Typography>
                {currentCarrier && (
                  <Typography variant="body2" color="primary.main" fontWeight="bold">
                    Testing: {currentCarrier}
                  </Typography>
                )}
                {currentStage && (
                  <Typography variant="caption" color="text.secondary">
                    Stage: {currentStage}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
                {carrierProgress.total > 0 && (
                  <Chip 
                    icon={<SignalIcon />} 
                    label={`Carrier ${carrierProgress.current}/${carrierProgress.total}`} 
                    color="primary" 
                    size="small"
                  />
                )}
                {currentIP && (
                  <Chip 
                    icon={<RouterIcon />} 
                    label={currentIP} 
                    color="info" 
                    variant="outlined"
                    size="small"
                  />
                )}
                {currentAttempt.max > 0 && (
                  <Chip 
                    icon={<RefreshIcon />} 
                    label={`Attempt ${currentAttempt.current}/${currentAttempt.max}`} 
                    color={currentAttempt.current > 20 ? 'error' : currentAttempt.current > 10 ? 'warning' : 'default'}
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            </Box>

            {/* Progress bars */}
            <Box sx={{ mb: 2 }}>
              {carrierProgress.total > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Overall Progress
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={(carrierProgress.current / carrierProgress.total) * 100} 
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
              )}
              {currentAttempt.max > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    ASN Hunt Progress (attempt {currentAttempt.current}/{currentAttempt.max})
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min((currentAttempt.current / currentAttempt.max) * 100, 100)} 
                    color={currentAttempt.current > 20 ? 'error' : currentAttempt.current > 10 ? 'warning' : 'primary'}
                    sx={{ height: 6, borderRadius: 1 }}
                  />
                </Box>
              )}
            </Box>

            <Grid container spacing={2}>
              {/* Live Progress Log */}
              <Grid item xs={12} md={latestScreenshot ? 7 : 12}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    maxHeight: 350, 
                    overflow: 'auto', 
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 1,
                  }}
                >
                  <List dense disablePadding>
                    {progressLogs.map((log, index) => (
                      <ListItem key={index} disablePadding sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {log.type === 'carrier' && <SignalIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
                      {log.type === 'ip' && <RouterIcon sx={{ fontSize: 16, color: 'info.main' }} />}
                      {log.type === 'retry' && <RefreshIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
                      {log.type === 'success' && <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />}
                      {log.type === 'blocked' && <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />}
                      {log.type === 'accessible' && <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />}
                      {log.type === 'warning' && <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
                      {log.type === 'error' && <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />}
                      {log.type === 'info' && <SpeedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                      {log.type === 'log' && <WifiIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            color: log.type === 'blocked' ? 'error.main' : 
                                   log.type === 'accessible' ? 'success.main' :
                                   log.type === 'retry' ? 'warning.main' :
                                   log.type === 'warning' ? 'warning.main' :
                                   log.type === 'error' ? 'error.main' :
                                   log.type === 'carrier' ? 'primary.main' :
                                   'text.primary'
                          }}
                        >
                              <Typography component="span" sx={{ color: 'text.secondary', mr: 1, fontSize: '0.75rem' }}>
                                [{log.time}]
                              </Typography>
                              {log.message}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                    <div ref={progressEndRef} />
                  </List>
                  {progressLogs.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                      Waiting for progress updates...
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Live Screenshot Preview */}
              {latestScreenshot && (
                <Grid item xs={12} md={5}>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 1, 
                      bgcolor: 'background.default',
                      height: 350,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      📷 Latest Screenshot: {latestScreenshotCarrier}
                    </Typography>
                    <Box 
                      component="img"
                      src={latestScreenshot}
                      alt="Latest screenshot"
                      sx={{ 
                        width: '100%', 
                        height: '100%',
                        objectFit: 'contain',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.9 }
                      }}
                      onClick={() => setScreenshotDialog({ 
                        open: true, 
                        url: latestScreenshot, 
                        title: latestScreenshotCarrier 
                      })}
                    />
                  </Paper>
                </Grid>
              )}
            </Grid>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Testing through mobile carrier proxies with ASN verification. Each carrier may take 30-60 seconds.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {results && !loading && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Analysis Results
            </Typography>

            {/* Summary - Calculate from results if summary is missing/wrong */}
            {(() => {
              // Calculate blocking stats from actual results
              const totalTested = results.results?.length || 0;
              const blockedCount = results.results?.filter(r => r.blocking?.detected).length || 0;
              const accessibleCount = results.results?.filter(r => r.success && !r.blocking?.detected).length || 0;
              const skippedCount = results.results?.filter(r => r.asnNotFound).length || 0;
              const failedCount = totalTested - accessibleCount - skippedCount;
              const hasBlocking = blockedCount > 0;
              
              return (
                <Paper 
                  sx={{ 
                    p: 2, 
                    mb: 3, 
                    bgcolor: hasBlocking ? 'error.dark' : 'success.dark',
                    color: 'white'
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item>
                      {hasBlocking ? (
                        <ErrorIcon sx={{ fontSize: 48 }} />
                      ) : (
                        <CheckCircleIcon sx={{ fontSize: 48 }} />
                      )}
                    </Grid>
                    <Grid item xs>
                      <Typography variant="h5">
                        {hasBlocking 
                          ? `Blocking Detected on ${blockedCount} Carrier(s)`
                          : 'No Blocking Detected'
                        }
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Tested {totalTested} carriers • 
                        {accessibleCount} accessible • 
                        {blockedCount} blocked • 
                        {skippedCount} skipped
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {results.tested_at ? new Date(results.tested_at).toLocaleString() : new Date().toLocaleString()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              );
            })()}

            {/* Blocking Types */}
            {results.blocking_types?.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Blocking Types Detected:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {results.blocking_types.map((type, i) => (
                    <Chip 
                      key={i} 
                      label={type} 
                      color="error" 
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Individual Results */}
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Carrier Results
            </Typography>

            {results.results?.map((result, index) => {
              const carrierInfo = getCarrierInfo(result.proxy);
              const isExpanded = expandedResults[index];

              return (
                <Paper 
                  key={index} 
                  variant="outlined" 
                  sx={{ 
                    mb: 2, 
                    overflow: 'hidden',
                    borderColor: result.blocking?.detected ? 'error.main' : 
                                 result.asnNotFound ? 'warning.main' : 
                                 result.success ? 'success.main' : 'error.main',
                    borderWidth: 2
                  }}
                >
                  <Box 
                    sx={{ 
                      p: 2, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    onClick={() => setExpandedResults({ ...expandedResults, [index]: !isExpanded })}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {getStatusIcon(result)}
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {getCountryFlag(result.proxy?.country)} {result.proxy?.label || 'Unknown Carrier'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {result.proxyInfo?.ip || 'N/A'} • {result.proxyInfo?.asn || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={getStatusText(result)} 
                        color={getStatusColor(result)}
                        size="small"
                      />
                      {/* Retry button for skipped carriers */}
                      {result.asnNotFound && (
                        <Tooltip title="Retry with more attempts">
                          <IconButton 
                            size="small"
                            color="warning"
                            onClick={(e) => {
                              e.stopPropagation();
                              openRetryDialog(index);
                            }}
                            disabled={retryingCarrier !== null}
                          >
                            {retryingCarrier?.index === index ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <RefreshIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      {result.browserResult?.screenshotPath && (
                        <Tooltip title="View Screenshot">
                          <IconButton 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openScreenshot(
                                result.browserResult.screenshotPath,
                                result.proxy?.label || 'Screenshot'
                              );
                            }}
                          >
                            <PhotoIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton size="small">
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>

                  <Collapse in={isExpanded}>
                    <Divider />
                    <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">Proxy Info</Typography>
                          <Typography variant="body2">
                            IP: {result.proxyInfo?.ip || 'N/A'}
                          </Typography>
                          <Typography variant="body2">
                            ASN: {result.proxyInfo?.asn || 'N/A'} 
                            {result.proxyInfo?.asnName && ` (${result.proxyInfo.asnName})`}
                          </Typography>
                          <Typography variant="body2">
                            ISP: {result.proxyInfo?.isp || 'N/A'}
                          </Typography>
                          {result.asnVerified && (
                            <Chip 
                              label="ASN Verified" 
                              color="success" 
                              size="small" 
                              sx={{ mt: 1 }}
                            />
                          )}
                          {result.doubleVerified && (
                            <Chip 
                              label="Double Verified" 
                              color="success" 
                              size="small" 
                              sx={{ mt: 1, ml: 1 }}
                            />
                          )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">Result Details</Typography>
                          {result.blocking?.detected ? (
                            <>
                              <Alert severity="error" sx={{ mt: 1 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.blocking.type}
                                </Typography>
                                {result.blocking.details && (
                                  <Typography variant="body2">
                                    {result.blocking.details}
                                  </Typography>
                                )}
                              </Alert>
                            </>
                          ) : result.asnNotFound ? (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              <Typography variant="body2">
                                {result.error || 'ASN not found in proxy pool'}
                              </Typography>
                            </Alert>
                          ) : result.success ? (
                            <Alert severity="success" sx={{ mt: 1 }}>
                              <Typography variant="body2">
                                Site loaded successfully
                              </Typography>
                              {result.browserResult?.pageTitle && (
                                <Typography variant="body2">
                                  Page Title: {result.browserResult.pageTitle}
                                </Typography>
                              )}
                            </Alert>
                          ) : (
                            <Alert severity="error" sx={{ mt: 1 }}>
                              <Typography variant="body2">
                                {result.error || 'Test failed'}
                              </Typography>
                            </Alert>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* ASN Probe Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RouterIcon color="secondary" />
            ASN Probe Tool
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Discover which ASNs are available through your NetNut proxy for any country. 
            This rotates through IPs and logs which ISPs/carriers you can actually use.
          </Typography>
          
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                size="small"
                options={allCountries.map(c => ({
                  code: c.code,
                  name: COUNTRY_NAMES[c.code] || c.code,
                  count: c.count
                }))}
                getOptionLabel={(option) => {
                  if (!option) return '';
                  const name = option.name || COUNTRY_NAMES[option.code] || option.code;
                  return `${getCountryFlag(option.code)} ${name} (${option.code})`;
                }}
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter(option => 
                    option.name.toLowerCase().includes(searchTerm) ||
                    option.code.toLowerCase().includes(searchTerm)
                  );
                }}
                value={probeCountry ? { 
                  code: probeCountry, 
                  name: COUNTRY_NAMES[probeCountry] || probeCountry,
                  count: allCountries.find(c => c.code === probeCountry)?.count || 0
                } : null}
                onChange={(event, newValue) => {
                  setProbeCountry(newValue?.code || '');
                }}
                disabled={probing}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country to Probe"
                    placeholder="Select country..."
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '1.2rem' }}>{getCountryFlag(option.code)}</Typography>
                    <Typography variant="body2">{option.name} ({option.code})</Typography>
                  </Box>
                )}
                isOptionEqualToValue={(option, value) => option?.code === value?.code}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                size="small"
                type="number"
                label="Rotations"
                value={probeRotations}
                onChange={(e) => setProbeRotations(Math.max(10, Math.min(200, parseInt(e.target.value) || 50)))}
                disabled={probing}
                inputProps={{ min: 10, max: 200 }}
                fullWidth
                helperText="10-200"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              {probing ? (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={stopProbe}
                  startIcon={<CloseIcon />}
                  fullWidth
                >
                  Stop Probe
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={startProbe}
                  disabled={!probeCountry}
                  startIcon={<RefreshIcon />}
                  fullWidth
                >
                  Start Probe
                </Button>
              )}
            </Grid>
          </Grid>
          
          {/* Probe Progress */}
          {(probing || probeResults) && (
            <>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Progress: {probeProgress.current} / {probeProgress.total} rotations
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Unique ASNs: {probeProgress.uniqueAsns}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(probeProgress.current / probeProgress.total) * 100}
                  color="secondary"
                />
              </Box>
              
              {/* Probe Logs */}
              <Paper 
                sx={{ 
                  p: 1, 
                  mb: 2, 
                  maxHeight: 150, 
                  overflow: 'auto',
                  bgcolor: 'background.default',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem'
                }}
              >
                {probeLogs.slice(-30).map((log, i) => (
                  <Box 
                    key={i} 
                    sx={{ 
                      color: log.type === 'error' ? 'error.main' : 
                             log.type === 'success' ? 'success.main' : 'text.secondary',
                      py: 0.25
                    }}
                  >
                    [{log.time.toLocaleTimeString()}] {log.message}
                  </Box>
                ))}
                <div ref={probeLogsEndRef} />
              </Paper>
            </>
          )}
          
          {/* Probe Results */}
          {probeResults && probeResults.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Available ASNs (sorted by frequency) - Click to add to analysis
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 200, overflow: 'auto' }}>
                {probeResults.map((asn, i) => (
                  <Chip
                    key={asn.asn}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                          {asn.percentage}%
                        </Typography>
                        <Typography variant="caption">
                          AS{asn.asn} - {asn.name?.substring(0, 25)}{asn.name?.length > 25 ? '...' : ''}
                        </Typography>
                      </Box>
                    }
                    size="small"
                    color={i < 3 ? 'secondary' : 'default'}
                    variant={selectedCarriers.some(c => c.asn === asn.asn) ? 'filled' : 'outlined'}
                    onClick={() => addProbeAsn(asn)}
                    sx={{ 
                      cursor: 'pointer',
                      opacity: selectedCarriers.some(c => c.asn === asn.asn) ? 0.6 : 1
                    }}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Top results have higher availability through your proxy. Click an ASN to add it to your analysis above.
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      {/* Screenshot Dialog */}
      <Dialog 
        open={screenshotDialog.open} 
        onClose={() => setScreenshotDialog({ open: false, url: '', title: '' })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {screenshotDialog.title}
          <IconButton onClick={() => setScreenshotDialog({ open: false, url: '', title: '' })}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {screenshotDialog.url && (
            <Box 
              component="img" 
              src={screenshotDialog.url} 
              alt="Screenshot"
              sx={{ 
                width: '100%', 
                height: 'auto',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}
              onError={(e) => {
                e.target.src = '';
                e.target.alt = 'Screenshot not available';
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Retry Skipped Carrier Dialog */}
      <Dialog 
        open={retryDialog.open} 
        onClose={() => setRetryDialog({ open: false, carrier: null, index: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RefreshIcon color="warning" />
            Retry Skipped Carrier
          </Box>
          <IconButton onClick={() => setRetryDialog({ open: false, carrier: null, index: null })}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The carrier <strong>{retryDialog.carrier}</strong> (AS{retryDialog.asn}) was skipped because the ASN couldn't be found within the retry limit.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You can retry with more attempts to increase the chance of finding this ASN in the proxy pool.
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Max Retry Attempts"
            value={retryMaxRetries}
            onChange={(e) => setRetryMaxRetries(Math.max(10, Math.min(100, parseInt(e.target.value) || 30)))}
            inputProps={{ min: 10, max: 100 }}
            helperText="Higher values increase chance of finding the ASN but take longer (10-100)"
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              onClick={() => setRetryDialog({ open: false, carrier: null, index: null })}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="warning"
              onClick={() => retrySkippedCarrier(retryDialog.index, retryMaxRetries)}
              startIcon={<RefreshIcon />}
            >
              Retry ({retryMaxRetries} attempts)
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Retry Progress Overlay */}
      {retryingCarrier && (
        <Dialog open={true} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              Retrying Carrier...
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Carrier: <strong>{currentCarrier}</strong>
              </Typography>
              {currentIP && (
                <Typography variant="body2" color="text.secondary">
                  Current IP: {currentIP}
                </Typography>
              )}
              {currentStage && (
                <Typography variant="body2" color="text.secondary">
                  Stage: {currentStage}
                </Typography>
              )}
            </Box>
            
            {currentAttempt.max > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  ASN Hunt Progress ({currentAttempt.current}/{currentAttempt.max})
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={(currentAttempt.current / currentAttempt.max) * 100}
                  color={currentAttempt.current > 50 ? 'error' : currentAttempt.current > 25 ? 'warning' : 'primary'}
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
            )}
            
            <Paper 
              sx={{ 
                p: 1, 
                maxHeight: 150, 
                overflow: 'auto',
                bgcolor: 'background.default',
                fontFamily: 'monospace',
                fontSize: '0.75rem'
              }}
            >
              {progressLogs.slice(-20).map((log, i) => (
                <Box key={i} sx={{ py: 0.25, color: 'text.secondary' }}>
                  [{log.time.toLocaleTimeString()}] {log.message}
                </Box>
              ))}
            </Paper>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default DomainAnalyzer;
